import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, LandingContent, Locale } from '@xdocs/schemas';
import { useWs } from '../../../ws/WsProvider.js';

export const ADMIN_CMS_BLOCKS_QUERY_KEY = ['admin-cms-blocks'] as const;

/** One block key's latest known row per locale (a locale may be missing —
 * F61 AC: "a missing block key on the landing... graceful"). */
export type CmsBlockGroup = Record<Locale, LandingContent | undefined> & { blockKey: string };

export interface UseCmsBlocksResult {
  groups: readonly CmsBlockGroup[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  invalidate: () => void;
}

/**
 * `admin.cms.blocksList` (F61) has no `listQueryInput` shape (just an
 * optional `locale` filter, `packages/schemas/src/admin.ts`) — it isn't a
 * paginated resource, so this fetches the full (small, staff-curated) set
 * unfiltered and groups client-side by `blockKey`, rather than bridging
 * `<DataTable>` (E4 §8.3 applies to queryable list ops; this one isn't
 * shaped as one).
 */
export function useCmsBlocks(): UseCmsBlocksResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected';

  const result = useQuery<{ blocks: LandingContent[] }, ErrorAlert>({
    queryKey: ADMIN_CMS_BLOCKS_QUERY_KEY,
    queryFn: () => requestTyped(wsClient, 'admin.cms.blocksList', {}),
    enabled,
  });

  const groups = useMemo<CmsBlockGroup[]>(() => {
    const byKey = new Map<string, CmsBlockGroup>();
    for (const block of result.data?.blocks ?? []) {
      const existing = byKey.get(block.blockKey) ?? ({ blockKey: block.blockKey } as CmsBlockGroup);
      // Later entries in an unordered response could be an older version —
      // keep the higher `version` per locale.
      const current = existing[block.locale];
      if (!current || current.version < block.version) {
        existing[block.locale] = block;
      }
      byKey.set(block.blockKey, existing);
    }
    return Array.from(byKey.values()).sort((a, b) => a.blockKey.localeCompare(b.blockKey));
  }, [result.data]);

  const onRetry = (): void => void result.refetch();
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ADMIN_CMS_BLOCKS_QUERY_KEY });

  return { groups, isLoading: result.isLoading, isError: result.isError, onRetry, invalidate };
}
